<template>
  <div :key="$store.getters['resource/currentResource'].key">
    <loader v-if="loading"/>
    <b-row>

      <b-col :md="columnWith">
        <label :for="'amount'">{{$t('transactions.amount')}}</label>
        <div class="mb-4 flex-row flex-nowrap d-flex">

          <b-input-group class="w-75">
            <money v-model="params.amount"
                   v-bind="money"
                   @input="value => {createParamsObject('amount', formatToCents(value))}"
                   class="rounded"
            ></money>
          </b-input-group>
          <multiselect
            class="ml-1 w-25"
            v-if="currencyOptions"
            :searchable="false"
            v-model="params.currency"
            @input="value => {createParamsObject('currency', value)}"
            label="label"
            track-by="label"
            placeholder=""
            :options="currencyOptions"
            :preselect-first="true"
            :allow-empty="false"
            select-label=""
            selected-label=""
            deselect-label=""
          >
            <template slot="noResult">
              {{$t('global.no_results')}}
            </template>
          </multiselect>
        </div>
      </b-col>

      <b-col
        class="mb-4"
        md="4"
      >
        <label>{{ $t('transactions.fee') }}</label>

        <b-input-group>
          <money v-model="params.fee"
                 v-bind="money"
                 @input="value => {createParamsObject('fee', formatToCents(value))}"
                 class="rounded"
          ></money>
        </b-input-group>
      </b-col>

      <b-col
        md="4"
        v-if="!!checkboxes.length"
        class="mb-4 d-flex align-items-center mt-3"
        v-for="(input, index) in checkboxes"
        :key="`${index}${input.name}`"
      >
        <b-form-checkbox
          class="form-check-input ml-0"
          v-model="params[input.name]"
        >
          {{$t('resources.live_mode')}}
        </b-form-checkbox>
      </b-col>

      <b-col
        class="mb-4"
        :md="columnWith"
        v-for="(input, index) in datepickers"
        :key="`${index}${input.name}`"
      >
        <label
          v-if="input.label"
        >{{ input.label }}</label>
        <v-date-picker
          :popover="{
            visibility: 'focus'
          }"
          :masks="{
            input: 'MM/DD/YYYY'
          }"
          v-model="params[input.name]"
          @input="value => {createParamsObject(input.name, value)}"
          :mode='input.mode'
          :is-range="input['is-range']"
        >
          <template v-slot="{inputEvents, inputValue}">
            <b-input v-on="inputEvents"
                     placeholder="MM/DD/YYYY"
                     :value="datePickerInputFormalize(inputValue)"
                     class="datepicker-readonly-input"
                     readonly/>
          </template>
        </v-date-picker>
      </b-col>

      <b-col
        :md="columnWith"
        v-if="selectOptions.user_id && !userPrefillId"
      >
        <label for="user_id">{{ $t('users.user') }}<span class="text-danger">{{$t('form.required_star')}}</span></label>
        <multiselect
          :loading="isSelectLoading.user_id"
          class="mb-4"
          v-model="params.user_id"
          id="user_id"
          label="label"
          track-by="label"
          :placeholder="$t('transactions.type_to_search')"
          :options="selectOptions.user_id"
          :searchable="true"
          @search-change="query => {debounceGetSelectOptions('users?limit=10', 'user_id', 'global', query)}"
          :close-on-select="true"
          @remove="value => {createParamsObject('user_id', value)}"
          @select="value => {createParamsObject('user_id', value)}"
          select-label=""
          selected-label=""
          deselect-label="X ">
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>
        </multiselect>
        <span class="text-danger position-absolute error"
              v-if="!$v.user_id.require && $v.user_id.$error">
          {{$t('form.required_field')}}
        </span>

      </b-col>

      <b-col
        :md="columnWith"
        v-for="(input, index) in groupSelects"
        :key="`${index}${input.name}`"
        v-if="groupMultiselects[input.name]"
      >
        <group-select
          class="mb-4"
          :input="input"
          :model="params[input.name]"
          :disabled="false"
          @select="createPurchases"
          @remove="createPurchases"
        />
      </b-col>
      <b-col
        :md="columnWith"
        class="mb-4"
        v-for="(input, index) in selects"
        :key="`${index}${input.name}`"
        v-if="selectOptions[input.name]"
      >
        <label :for="input.name">{{ input.label }}{{input.required ? $t('form.required_star') : '' }}</label>
        <multiselect
          :loading="isSelectLoading[input.name]"
          v-model="params[input.name]"
          :multiple="input.multiple"
          :id="input.name"
          label="label"
          track-by="label"
          :placeholder="input.placeholder"
          :options="input.options || selectOptions[input.name]"
          :searchable="input.searchable"
          @search-change="query => {debounceGetSelectOptions(input.options_url, input.name, input.filter_field, query)}"
          :allow-empty="true"
          :taggable="input.taggable"
          :close-on-select="!input.multiple"
          @remove="value => {input.name === 'user_id' ? createParamsObject(input.name, value) : createPurchases(input.name, value)}"
          @select="value => {input.name === 'user_id' ? createParamsObject(input.name, value) : createPurchases(input.name, value)}"
          :disabled="false"
          select-label=""
          selected-label=""
          deselect-label="X ">
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>
        </multiselect>
      </b-col>


      <span class="text-danger position-absolute error"
            v-if="!$v.purchases.required && $v.purchases.$dirty">
            {{$t('form.required_field_purchases')}}
      </span>

    </b-row>

    <form-buttons @submit="debounceSubmit" v-if="showButtons" :show_delete_button="editing"
                  :delete_url="`transactions/${$route.params.id}`"></form-buttons>

  </div>
</template>

<script>
  import {filter, forEach, isObject, pull, debounce} from 'lodash';
  import {required} from "vuelidate/lib/validators";
  import {Money} from 'v-money'
  import {datePickerMethods} from "../../mixins/date";

  export default {
    name: 'TransactionView',
    middleware: 'auth',
    props: {
      fields: {
        type: Array,
        require: true
      },
      editing: {
        type: Boolean,
        default: false
      },
      columnWith: {
        type: Number,
        default: 4
      },
      userPrefillId: {
        type: String
      },
      showButtons: {
        type: Boolean,
        default: true
      },
      fromUserId: {
        type: String
      },
    },
    mixins: [datePickerMethods],
    data() {
      return {
        prefillParams: {},
        loading: false,
        isSelectLoading: {},
        selectLoading: false,
        user_id: null,
        purchases: [],
        loadingFromUser: false,
        money: {
          decimal: '.',
          thousands: ',',
          prefix: '',
          suffix: '',
          precision: 2,
          masked: false
        },
        params: {
          fee: '',
          amount: '',
          currency: {
            label: "USD",
            value: "USD"
          },
          user_id: null,
          purchases: [],
          created_at: null,
          content: [],
          bundle: [],
          subscription: [],
          livemode: true,
          ti_event_ticket_pass: [],
        },
        selectOptions: {
          user_id: []
        },
        paramsMutated: {},
        email_regex: [],
        selectsReady: false,
        groupMultiselects: {
          subscription: null,
          ti_event_ticket_pass: null
        },
        debounceGetSelectOptions: () => {
        },
        debounceSubmit: () => {
        }
      }
    },
    validations: {
      purchases: {
        required
      },
      user_id: {
        required
      }
    },
    components: {Money},
    mounted() {
      this.$store.dispatch('currency/getCurrency');
      const fromUserId = this.$route.query.fromUserId || this.fromUserId;
      if (this.editing) {
        this.getData(`transactions/${this.$route.params.id}`, null, true)
            .then(({ data }) => {
              console.log('User data (create):', data);
              this.prefillParams = data;
              this.setPrefillData(this.prefillParams, fromUserId);
            });
      }
      if (!this.editing && fromUserId && !this.userPrefillId) {
        this.loadingFromUser = true;
        this.getData(`users/${fromUserId}`, null, true)
            .then(({ data }) => {
              const userOption = {
                label: `${data.first_name} ${data.last_name}, ${data.email}`,
                value: data.id
              };
              this.$set(this.params, 'user_id', userOption);
              this.createParamsObject('user_id', userOption);
              this.$set(this.selectOptions, 'user_id', [
                ...(this.selectOptions.user_id || []),
                userOption
              ]);
            })
            .finally(() => {
              this.loadingFromUser = false;
            });
      }
      this.getSelectOptions('users?limit=10', 'user_id');
      this.debounceGetSelectOptions = debounce(this.getSelectOptions, 500);
      this.debounceSubmit = debounce(this.submit, 1000);
      this.$bus.$on(`submit-transactions-form`, () => {
        this.debounceSubmit();
      });
    },
    computed: {
      currencyOptions() {
        return this.$store.getters['currency/currency']
      },
      selects() {
        let selects = filter(this.fields, input => input.type === 'select' && !input.group);

        forEach(selects, select => {
          if (!!select.options_url) {
            this.getSelectOptions(select.options_url, select.name)
            // this.selectOptions[select.name] = [];
          } else {
            this.selectOptions[select.name] = select.options;
          }
        });
        return selects
      },
      datepickers() {
        return filter(this.fields, input => input.type === 'date');
      },
      checkboxes() {
        return filter(this.fields, input => input.type === 'checkbox');
      },
      groupSelects() {
        let selects = filter(this.fields, input => input.type === 'select' && input.group);

        selects.map(select => {
          let name = select.name;
          let subfield = name === 'subscription' ? 'subscriptions' : 'tickets';
          if (select.options_url) {
            this.loading = true;
            this.getData(select.options_url).then(({data}) => {
              return data.items.map(item => {
                return {
                  groupLabel: item.title,
                  groupOptions: item[subfield].map(groupOpts => {
                    return {
                      label: groupOpts.title,
                      value: groupOpts.id
                    }
                  })
                }
              });
            }).then(data => {
              this.groupMultiselects[name] = data;
              this.loading = false;
            })
          } else {
            this.groupMultiselects[name] = select.options
          }
        });
        return selects
      },
    },
    methods: {
      createParamsObject(name, value) {
        if (isObject(name)) {
          value = name.value;
          name = name.field;
        }
        if (isObject(value)) {
          if (value instanceof Date) {
            value = this.formatDate(value, 'L');
            this.paramsMutated[name] = value;
          }
          if (Array.isArray(value)) {
            this.paramsMutated[name] = [];
            value.map(item => {
              if (this.paramsMutated[name].indexOf(item.value) > -1) {
                pull(this.paramsMutated[name], item.value);
              } else {
                this.paramsMutated[name].push(item.value);
              }
            })
          }
          if (value.hasOwnProperty('value')) {
            value = value.value;
            this.paramsMutated[name] = value;
          }
        } else if (typeof value === 'number' && !Number.isInteger(value)) {
          this.paramsMutated[name] = Number(value.toFixed());
        } else {
          this.paramsMutated[name] = value;
        }
      },
      addPurchase(name, value) {
        this.params.purchases.push({
          item_type: name,
          item_key: value
        });
      },
      checkPurchase(value) {
        let id = null;
        this.params.purchases.map(purchase => {
          if (purchase.item_key === value) {
            id = this.params.purchases.indexOf(purchase)
          }
        });
        return id
      },
      createPurchases(name, value) {
        if (isObject(name)) {
          value = name.value;
          name = name.field
        }
        if (!_.isEmpty(this.params.purchases)) {
          if (this.checkPurchase(value.value) !== null) {
            this.params.purchases.splice(this.checkPurchase(value.value), 1);
          } else {
            this.addPurchase(name, value.value)
          }
        } else {
          this.addPurchase(name, value.value)
        }
        this.paramsMutated.purchases = this.params.purchases;
      },
      getSelectOptions(url, name, filter_field, value) {
        this.$set(this.isSelectLoading, name, true);
        const params = {filter: {}};
        params.filter[filter_field] = value;
        this.getData(url, {params}).then(({data}) => {
          this.selectOptions[name] = [];
          if (data && data.items) {
            this.selectOptions[name] = data.items.map(option => {
              if (name === 'user_id') {
                return {
                  label: `${option.first_name} ${option.last_name}, ${option.email}`,
                  value: option.id
                }
              }
              if (option.key) {
                return {
                  label: option.title,
                  value: option.key
                }
              } else if (option.first_name && option.id) {
                return {
                  label: `${option.first_name} ${option.last_name}`,
                  value: option.id
                }
              } else if (option.id) {
                return {
                  label: option.title,
                  value: option.id
                }
              } else if (option.title) {
                return {
                  label: option.title,
                  value: option.title
                }
              }
            });
          }
        }).then(() => {
          this.$forceUpdate();
          this.$set(this.isSelectLoading, name, false);
        })
      },
      setPrefillData(prefill, fromUserId) {
        this.params.amount = this.formatToDollars(prefill.amount);
        this.params.fee = this.formatToDollars(prefill.fee);
        if (prefill.currency) {
          this.params.currency = {
            label: prefill.currency,
            value: prefill.currency
          };
        }
        if (fromUserId) {
          this.loadingFromUser = true;
          this.getData(`users/${fromUserId}`, null, true)
              .then(({ data }) => {
                const userOption = {
                  label: `${data.first_name} ${data.last_name}, ${data.email}`,
                  value: data.id
                };
                this.$set(this.params, 'user_id', userOption);
                this.createParamsObject('user_id', userOption);
                this.$set(this.selectOptions, 'user_id', [
                  ...(this.selectOptions.user_id || []),
                  userOption
                ]);
              })
              .finally(() => {
                this.loadingFromUser = false;
              });
        } else if (prefill.user) {
          const userOption = {
            label: `${prefill.user.first_name} ${prefill.user.last_name}`,
            value: prefill.user.id
          };
          this.$set(this.params, 'user_id', userOption);
          this.createParamsObject('user_id', userOption);
          this.$set(this.selectOptions, 'user_id', [
            ...(this.selectOptions.user_id || []),
            userOption
          ]);
        }
        if (prefill.created_at) {
          this.params.created_at = new Date(prefill.created_at);
        }
        this.params.livemode = prefill.livemode;
        prefill.purchases.forEach(purchase => {
          this.addPurchase(purchase.item_type, purchase.item_key);
          this.params[purchase.item_type].push({
            label: purchase.item_title,
            value: purchase.item_key
          });
        });
      },
      submit() {
        this.amount = this.params.amount;
        this.user_id = this.params.user_id;
        this.purchases = this.params.purchases;
        this.paramsMutated.livemode = this.params.livemode;

        this.paramsMutated.currency = this.params.currency ? this.params.currency.value : 'USD';

        if (this.userPrefillId) {
          this.user_id = this.userPrefillId;
          this.paramsMutated.user_id = this.userPrefillId
        }

        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }
        if (!_.isEmpty(this.prefillParams)) {
          this.patchData(`transactions/${this.prefillParams.id}`, this.paramsMutated, '/transactions')
        } else {
          this.postData('transactions', this.paramsMutated).then(resp => {
            if (resp) {
              if (!this.userPrefillId) {
                this.$router.push({path: '/transactions'})
              } else {
                this.$emit('submit')
              }
              this.$bus.$emit('closeCreateTransactionModal');
            }
          }).then(() => {
            this.params = {
              fee: '',
              amount: '',
              currency: '',
              user_id: null,
              purchases: [],
              created_at: null,
              content: [],
              bundle: [],
              subscription: [],
              livemode: true,
              ti_event_ticket_pass: []
            }
          })
        }
      }
    },
    destroyed() {
      this.$bus.$off(`submit-transactions-form`);
    }
  }
</script>
